import isNil from 'lodash-es/isNil'
import mapValues from 'lodash-es/mapValues'
import omit from 'lodash-es/omit'
import omitBy from 'lodash-es/omitBy'
import orderBy from 'lodash-es/orderBy'
import pick from 'lodash-es/pick'

import {
  APPOINTMENT_MOCK_LIST_ANCHOR_FIELDS,
  APPOINTMENT_MOCK_LIST_INTERNAL_FIELDS,
  APPOINTMENT_MOCK_LIST_PAST_ORDER,
  APPOINTMENT_MOCK_LIST_TIE_FIELD,
  APPOINTMENT_MOCK_LIST_UPCOMING_ORDER,
  APPOINTMENT_MOCK_SCOPE_PARAM_COUNT,
  APPOINTMENTS_FIRST_PAGE,
  APPOINTMENTS_LIST_ENDPOINT,
  APPOINTMENTS_PAGE_SIZE_MAX,
  APPOINTMENTS_TAB_PAGE_SIZE,
  AppointmentsScopeParam,
} from './Appointment.constants'
import { getIsAppointmentCancelled } from './Appointment.utils'

function getAppointmentMockScopeParams(params) {
  return Object.values(AppointmentsScopeParam).filter(
    param => !isNil(params[param])
  )
}

function getAppointmentMockScopeValue({ record, scopeParam }) {
  switch (scopeParam) {
    case AppointmentsScopeParam.building:
      return record.anchor.building?.id
    case AppointmentsScopeParam.emailThread:
      return record.email_thread?.id
    case AppointmentsScopeParam.flat:
      return record.anchor.flat?.id
    case AppointmentsScopeParam.resident:
      return record.anchor.resident?.id
    default:
      return record.anchor.request?.id
  }
}

function getIsAppointmentMockCancelledIncluded(params) {
  return String(params.include_cancelled) !== String(false)
}

function getIsAppointmentMockListed({ params, record, scopeParam }) {
  return (
    !record.deletedAt &&
    String(getAppointmentMockScopeValue({ record, scopeParam })) ===
      String(params[scopeParam]) &&
    (getIsAppointmentMockCancelledIncluded(params) ||
      !getIsAppointmentCancelled(record.status))
  )
}

function getAppointmentMockStartTime(item) {
  return new Date(item.start_at).getTime()
}

function toAppointmentMockListItem({ asOfTime, record }) {
  return {
    anchor: mapValues(
      APPOINTMENT_MOCK_LIST_ANCHOR_FIELDS,
      (fields, name) => record.anchor[name] && pick(record.anchor[name], fields)
    ),
    appointment_type: record.appointment_type,
    can_edit: record.can_edit,
    end_at: record.end_at,
    id: record.id,
    is_all_day: record.is_all_day,
    is_upcoming: new Date(record.end_at).getTime() >= asOfTime,
    numericId: record.numericId,
    participant_count: record.participants.length,
    start_at: record.start_at,
    status: record.status,
    title: record.title,
  }
}

function getAppointmentMockOrderedItems(items) {
  return [
    ...orderBy(
      items.filter(item => item.is_upcoming),
      [getAppointmentMockStartTime, APPOINTMENT_MOCK_LIST_TIE_FIELD],
      APPOINTMENT_MOCK_LIST_UPCOMING_ORDER
    ),
    ...orderBy(
      items.filter(item => !item.is_upcoming),
      [getAppointmentMockStartTime, APPOINTMENT_MOCK_LIST_TIE_FIELD],
      APPOINTMENT_MOCK_LIST_PAST_ORDER
    ),
  ]
}

export function getIsAppointmentMockListParamsValid(params) {
  return (
    getAppointmentMockScopeParams(params).length ===
    APPOINTMENT_MOCK_SCOPE_PARAM_COUNT
  )
}

export function getAppointmentMockListPage({ params, records }) {
  const [scopeParam] = getAppointmentMockScopeParams(params)

  const asOf = params.as_of ?? new Date().toISOString()

  const page = Number(params.page ?? APPOINTMENTS_FIRST_PAGE)

  const pageSize = Math.min(
    Number(params.page_size ?? APPOINTMENTS_TAB_PAGE_SIZE),
    APPOINTMENTS_PAGE_SIZE_MAX
  )

  const items = getAppointmentMockOrderedItems(
    records
      .filter(record =>
        getIsAppointmentMockListed({ params, record, scopeParam })
      )
      .map(record =>
        toAppointmentMockListItem({
          asOfTime: new Date(asOf).getTime(),
          record,
        })
      )
  )

  const offset = (page - APPOINTMENTS_FIRST_PAGE) * pageSize

  return {
    as_of: asOf,
    items: items
      .slice(offset, offset + pageSize)
      .map(item => omit(item, APPOINTMENT_MOCK_LIST_INTERNAL_FIELDS)),
    next:
      offset + pageSize < items.length
        ? `${APPOINTMENTS_LIST_ENDPOINT}?${new URLSearchParams({ ...omitBy(params, isNil), as_of: asOf, page: page + 1 })}`
        : null,
    total: items.length,
  }
}
